"use client";
import PromoBanner from "@/components/PromoBanner";
import { convertKeysToCamelCase, ToSnakeCase } from "@/utils/shortCode";
import type { PromoBannerProps, PromoBannerVariant } from "./types";
import type { Image, CTA } from "@/types";
import { usePathname } from "next/navigation";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type {
  UmbrellaPromoPageTestResult,
  SlimTaggedPromoBannerItem,
} from "@/monarch/rules/umbrellaPromoPage/types";
import { useEffect, useRef } from "react";

// Module-level counter to track the order of PromoBanner components on the page
let bannerOrderCounter = 0;

// Reset counter on page navigation (client-side only)
if (typeof window !== "undefined") {
  // Reset counter when page loads
  if (window.history.state?.bannerCounterReset !== true) {
    bannerOrderCounter = 0;
    window.history.replaceState(
      { ...window.history.state, bannerCounterReset: true },
      "",
    );
  }
}

type ShortCodePromoBannerProps = ToSnakeCase<PromoBannerProps> & {
  variant?: PromoBannerVariant;
  image_url?: string;
  image_alt_text?: string;
  image_width?: string;
  image_height?: string;
  use_default_discount_text?: "true" | "false";
  cta_label?: string;
  cta_url?: string;
  cta_location?: string;
  cta_html_id?: string;
  tooltip_info_text?: string;
  tag_text?: string;
  tag_icon?: string;
  product_type?: string;
};

// Helper function to convert shortcode props to PromoBannerProps
function convertShortcodeProps(
  props: ShortCodePromoBannerProps,
): PromoBannerProps {
  const camelCaseProps = convertKeysToCamelCase(props) as any;

  // Handle image - convert from string props to Image object if provided
  if (props.image_url) {
    const image: Image = {
      url: props.image_url,
      altText: props.image_alt_text || "",
    };
    if (props.image_width) {
      image.width = Number(props.image_width);
    }
    if (props.image_height) {
      image.height = Number(props.image_height);
    }
    camelCaseProps.image = image;
  }

  // Convert use_default_discount_text from string to boolean
  if (props.use_default_discount_text !== undefined) {
    camelCaseProps.useDefaultDiscountText =
      props.use_default_discount_text === "true";
  }

  // Handle CTA if provided as separate props
  if (props.cta_label && props.cta_url) {
    const cta: CTA = {
      label: props.cta_label,
      url: props.cta_url,
    };
    if (props.cta_location) {
      cta.location = props.cta_location;
    }
    if (props.cta_html_id) {
      cta.html_id = props.cta_html_id;
    }
    camelCaseProps.cta = cta;
  }

  // Handle tooltip if provided
  if (props.tooltip_info_text) {
    camelCaseProps.tooltip = {
      infoText: props.tooltip_info_text,
    };
  }

  return camelCaseProps as PromoBannerProps;
}

// Helper function to render PromoBanner with Monarch data
function renderPromoBannerWithMonarchData(
  testResult: UmbrellaPromoPageTestResult | null | undefined,
  props: ShortCodePromoBannerProps,
  autoIndex?: number,
) {
  // Convert shortcode props first (always use as fallback)
  const shortcodeProps = convertShortcodeProps(props);

  // Ensure variant is slimTagged for fallback
  const fallbackProps: PromoBannerProps = {
    ...shortcodeProps,
    variant: "slimTagged",
  };

  // If no testResult, empty object, or no data, use shortcode props as fallback
  if (
    !testResult ||
    !testResult.data ||
    !testResult.data.banners ||
    !Array.isArray(testResult.data.banners) ||
    testResult.data.banners.length === 0
  ) {
    return <PromoBanner {...fallbackProps} />;
  }

  const bannerGroups = testResult.data.banners;

  // Helper to check if banner group item is valid
  const isValidBannerItem = (
    item: unknown,
  ): item is SlimTaggedPromoBannerItem =>
    !!(item && typeof item === "object" && Object.keys(item).length > 0);

  // Only use Monarch data if banners has exactly 2 valid elements
  const validBanners = bannerGroups.filter((group) =>
    isValidBannerItem(group?.item),
  );

  if (validBanners.length !== 2) {
    return <PromoBanner {...fallbackProps} />;
  }

  // Use automatic order tracking index
  const validIndex = autoIndex ?? 0;

  // Extract items from all banner groups into a single array
  const allItems = bannerGroups
    .map((group) => group.item)
    .filter(isValidBannerItem);

  // Get the specific item from the array based on index
  const monarchData = allItems[validIndex];

  // If no valid item found at the specified index, use shortcode props as fallback
  if (!monarchData) {
    return <PromoBanner {...fallbackProps} />;
  }

  // Transform Monarch data structure to match component props
  const monarchOnlyProps: PromoBannerProps = {
    tagText: monarchData.tagText,
    tagIcon: monarchData.tagIcon,
    productType: monarchData.productType,
    title: monarchData.title,
    description: monarchData.description,
    discountCode: monarchData.discountCode,
    endDate: monarchData.endDate,
    // Transform imageUrl and imageAltText to Image object
    image: monarchData.imageUrl
      ? {
          url: monarchData.imageUrl,
          altText: monarchData.imageAltText || "",
        }
      : undefined,
    // Transform tooltip string to object with infoText
    tooltip: monarchData.tooltip
      ? { infoText: monarchData.tooltip }
      : undefined,
    variant: "slimTagged",
  };

  // Wrap PromoBanner with id attribute if provided from Monarch
  if (monarchData.id) {
    return (
      <div id={monarchData.id}>
        <PromoBanner {...monarchOnlyProps} />
      </div>
    );
  }

  return <PromoBanner {...monarchOnlyProps} />;
}

export function ShortCodePromoBanner(props: ShortCodePromoBannerProps) {
  const pathname = usePathname();
  const isOffersPage = pathname === "/offers" || pathname?.endsWith("/offers");
  const isSlimTagged = props.variant === "slimTagged";

  // Track order for automatic index assignment (only for Monarch-enabled banners)
  const orderRef = useRef<number | null>(null);

  useEffect(() => {
    // Reset counter when pathname changes (new page)
    bannerOrderCounter = 0;
  }, [pathname]);

  // Get automatic index based on render order
  if (isOffersPage && isSlimTagged && orderRef.current === null) {
    orderRef.current = bannerOrderCounter++;
  }

  // If on /offers page with slimTagged variant, use Monarch
  if (isOffersPage && isSlimTagged) {
    const autoIndex = orderRef.current ?? undefined;
    const renderMonarchBanner = (
      testResult: UmbrellaPromoPageTestResult | null,
    ) => renderPromoBannerWithMonarchData(testResult, props, autoIndex);

    const monarchBannerComponent = (
      <MonarchDataConsumer<UmbrellaPromoPageTestResult>
        experimentKey="useUmbrellaPromoPage"
        render={(testResult) => renderMonarchBanner(testResult || null)}
      />
    );

    return (
      <MonarchResolver<UmbrellaPromoPageTestResult>
        experimentKey="useUmbrellaPromoPage"
        Loader={<></>}
        variants={{
          control: monarchBannerComponent,
          test: monarchBannerComponent,
        }}
        fallbackVariant="control"
      />
    );
  }

  // Default behavior (without Monarch)
  const convertedProps = convertShortcodeProps(props);
  return <PromoBanner {...convertedProps} />;
}
